import React, { useState, useRef, useEffect } from 'react'
import { useStateValue } from '@State'
import { QUESTION_COMPONENTS, UnsupportedQuestion } from './questionRegistry'
import { applyBootstrapStyles } from './utils/applyBootstrapStyles'
import { Tabs } from './utils/Tabs'
import { casesetSubmitQuestion } from '../../api'
import { NGNCasesetFinished } from './NGNCasesetFinished'

// Container query, not a media query: the card's width depends on the course sidebars too.
const CASESET_STYLES = `
.ngn-caseset { container-type: inline-size; }
.ngn-caseset-body { display: flex; min-height: 400px; }
.ngn-caseset-left {
  width: 40%;
  border-right: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-width: 0;
  overflow-x: auto;
}
@container (max-width: 700px) {
  .ngn-caseset-body { flex-direction: column; }
  .ngn-caseset-left { width: 100%; border-right: none; border-bottom: 1px solid var(--border-subtle); }
}
`

export const NGNCasesetQuestion =({ question, startNumber }) => {
  const [state, dispatch] = useStateValue()
  const { readonly, coursenum, activitySubmissionId } = state
  const { title, questions, stimuli } = question
  const subQuestions = questions || []
  const containerRef = useRef(null)
  const allSubmitted = subQuestions.length > 0 && subQuestions.every(subQ => subQ.ngnCasesetSubmitted)
  const [finished, setFinished] = useState(allSubmitted)
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (allSubmitted) return subQuestions.length - 1
    const idx = subQuestions.findIndex(subQ => !subQ.ngnCasesetSubmitted)
    return idx === -1 ? subQuestions.length - 1 : idx
  })
  const [showWarning, setShowWarning] = useState(false)
  const [submittingNext, setSubmittingNext] = useState(false)

  useEffect(() => {
    if (containerRef.current) {
      applyBootstrapStyles(containerRef.current)
    }
  }, [stimuli])

  useEffect(() => {
    if (containerRef.current && window.MathLive?.me_renderMathInElement) {
      window.MathLive.me_renderMathInElement(containerRef.current)
    }
  }, [stimuli])

  const subQ = subQuestions[currentIndex]
  const isLast = currentIndex === subQuestions.length - 1

  if (!subQ) return null

  const SubComponent = QUESTION_COMPONENTS[subQ.type] ?? UnsupportedQuestion

  const submitCurrentQuestion = async (onComplete) => {
    if (!readonly && !subQ.ngnCasesetSubmitted) {
      setSubmittingNext(true)
      let submitted = false
      try {
        await casesetSubmitQuestion(coursenum, activitySubmissionId, subQ.guid)
        dispatch({ type: 'SET_CASESET_SUBMITTED', guid: subQ.guid })
        submitted = true
      } catch (err) {
        console.error('Failed to mark caseset question as submitted', err)
      } finally {
        setSubmittingNext(false)
        if (submitted) onComplete?.()
      }
    } else {
      onComplete?.()
    }
  }

  const handleNext = async () => {
    if (!readonly && !subQ.fullyAnswered) {
      setShowWarning(true)
      return
    }
    setShowWarning(false)
    await submitCurrentQuestion(() => setCurrentIndex(i => i + 1))
  }

  const handleFinish = async () => {
    if (!readonly && !subQ.fullyAnswered) {
      setShowWarning(true)
      return
    }
    setShowWarning(false)
    await submitCurrentQuestion(() => setFinished(true))
  }

  return (
    <div className="ngn-caseset" style={{
      border: `2px solid  #dee2e6`,
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '1rem',
    }}>
      {title && (
        <h3
          className='mt-0 mb-0 text-left'
          style={{
            backgroundColor: '#f8f9fa',
            color: '#212529',
            padding: '0.75rem 1rem',
            fontWeight: 'bold',
            borderBottom: `1px solid #dee2e6`,
          }}>
          Caseset
        </h3>
      )}
      <style>{CASESET_STYLES}</style>
      <div className="ngn-caseset-body">
        {/* Left panel: stimulus + question tabs */}
        <div className="ngn-caseset-left">
          {stimuli && (
            <div
              ref={containerRef}
              className="ngn-caseset-stimuli"
              dangerouslySetInnerHTML={{ __html: stimuli }}
              style={{
                padding: '1rem',
                borderBottom: '1px solid #dee2e6',
                overflowX: 'auto',
                overflowY: 'auto',
                flexShrink: 0,
              }}
            />
          )}
          {subQ.tabs && subQ.tabs.length > 0 && (
            <div className="p-3">
              <Tabs
                key={`${subQ.guid}-tabs`}
                tabs={subQ.tabs}
                questionGuid={subQ.guid}
              />
            </div>
          )}
        </div>

        {/* Right panel: active question or finished state */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          {finished ? (
            <div style={{ flex: 1, padding: '1rem', overflowY: 'auto' }}>
              <NGNCasesetFinished questionCount={subQuestions.length} />
            </div>
          ) : (
            <>
              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', minHeight: 0 }}>
<div key={subQ.guid} data-guid={subQ.guid}>
                  <SubComponent question={subQ} number={startNumber + currentIndex} renderTabs={false} />
                </div>
                {showWarning && (
                  <div className="alert alert-warning mt-3 mb-0" role="alert">
                    Please fully answer this question before moving on. You will not be able to come back.
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-between align-items-center" style={{ padding: '0.75rem 1rem', flexShrink: 0 }}>
                <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                  Question {currentIndex + 1} of {subQuestions.length}
                </span>
                {!readonly && (isLast ? (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleFinish}
                    disabled={submittingNext}
                  >
                    {submittingNext ? 'Saving...' : 'Finish Caseset'}
                  </button>
                ) : (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleNext}
                    disabled={submittingNext}
                  >
                    {submittingNext ? 'Saving...' : 'Next'}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
